interface FormatDownloadButtonsProps {
  /**
   * format -> media path, exactly as the API returns it. A "png" entry is the
   * on-screen preview rather than something to download, so it is dropped
   * here -- both callers used to filter it themselves, inconsistently.
   */
  files: Record<string, string>;
  onDownload: (format: string, path: string) => void;
  /**
   * Sorted first, so it gets the filled button -- e.g. "dst" on
   * /convert/pes-to-dst, where that's the file the visitor came for.
   */
  preferred?: string;
  /**
   * Adds an "Email me this design" button under the downloads, called with
   * the top format. Pages pass it only while the post-download email modal
   * is capped (see emailPromptStorage), so it's never offered twice at once.
   */
  onEmailDesign?: (format: string) => void;
}

// Most-wanted first. A format the backend starts returning later still gets a
// button, it just sorts after the ones we know how to rank.
const FORMAT_ORDER = ["pes", "dst", "exp", "jef", "vp3"];

const rank = (format: string) => {
  const index = FORMAT_ORDER.indexOf(format.toLowerCase());
  return index === -1 ? FORMAT_ORDER.length : index;
};

/**
 * The format downloads, shared by /create and /convert so the two pages
 * cannot drift apart.
 *
 * One filled button, the rest outlined: five equally-weighted full-width bars
 * told someone who owns a single machine nothing about which file they want.
 * PES leads because Brother machines are the common case, and it is first in
 * FORMAT_ORDER for exactly that reason.
 */
export function FormatDownloadButtons({ files, onDownload, preferred, onEmailDesign }: FormatDownloadButtonsProps) {
  const isPreferred = (format: string) => (format.toLowerCase() === preferred ? 0 : 1);
  const entries = Object.entries(files)
    .filter(([format]) => format.toLowerCase() !== "png")
    .sort(([a], [b]) => isPreferred(a) - isPreferred(b) || rank(a) - rank(b) || a.localeCompare(b));

  if (entries.length === 0) return null;

  return (
    <div className="format-download-grid">
      {entries.map(([format, path], index) => (
        <button
          key={format}
          type="button"
          className={index === 0 ? undefined : "outline"}
          onClick={() => onDownload(format, path)}
        >
          Download {format.toUpperCase()}
        </button>
      ))}
      {onEmailDesign && (
        <button
          type="button"
          className="outline format-download-email"
          onClick={() => onEmailDesign(entries[0][0])}
        >
          Email me this design
        </button>
      )}
    </div>
  );
}
